// react
import React, { useMemo } from 'react';

// components
import { Icon } from '@/components/atoms/Icon/Icon';

// store
import { Article } from '@karehero/models';

// utils
import { buildImageUrl } from '@/utils/sanity';

// mui
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

const FALLBACK_IMAGE =
  'https://cdn.sanity.io/images/x7r1t5o8/production/e8579e2a4c3766cf0cf99a24a9d0679d2d2bbeb4-5322x2365.jpg?h=150';

/**
 * ArticleCard is a container for the content of a Card.
 */
export const ArticleCard = ({
  article,
  onBookmark,
  onClick,
  isBookmarked = false,
  isLoading,
}: ArticleCardProps) => {
  const headerImageUrl = useMemo(
    () => buildImageUrl(article?.headerImageUrl, 500),
    [article?.headerImageUrl],
  );

  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{
          minWidth: 250,
          height: 300,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Skeleton variant='rectangular' height={150} />
        <CardContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          <Skeleton variant='text' width='60%' />
          <Skeleton variant='text' />
          <Skeleton variant='text' />
          <Skeleton variant='text' width='80%' />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      onClick={() => onClick?.(article)}
      sx={{
        minWidth: 250,
        height: 300,
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': { transform: 'scale(1.05)' },
      }}
    >
      <CardMedia
        component='img'
        height={150}
        image={headerImageUrl}
        alt='header'
        loading='lazy'
        onError={({
          currentTarget,
        }: React.SyntheticEvent<HTMLImageElement>) => {
          currentTarget.onerror = null;
          currentTarget.src = FALLBACK_IMAGE;
        }}
        sx={{ objectFit: 'cover', userSelect: 'none' }}
      />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 1,
          px: 1.5,
          py: 2,
          '&:last-child': { pb: 2 },
        }}
      >
        <Stack
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant='h4'
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              flex: 1,
              minWidth: 0,
            }}
          >
            {article?.title}
          </Typography>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              article && onBookmark?.(article);
            }}
            sx={{
              color: isBookmarked ? 'primary.main' : 'text.secondary',
              flexShrink: 0,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Icon icon='bookmark' type={isBookmarked ? 'fas' : 'fal'} />
          </IconButton>
        </Stack>
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
            display: '-webkit-box',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {article?.description}
        </Typography>
        {article?.tags && article.tags.length > 0 && (
          <Stack
            direction='row'
            spacing={0.5}
            useFlexGap
            sx={{
              flexWrap: 'wrap',
              mt: 'auto',
            }}
          >
            {article.tags.map((tag) => (
              <Chip
                key={`${article.title}-${tag.label}`}
                label={tag.label}
                size='small'
                variant='outlined'
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

interface ArticleCardProps {
  article?: Partial<Article>;
  onBookmark?: (article: Partial<Article>) => void;
  onClick?: (article?: Partial<Article>) => void;
  isBookmarked?: boolean;
  isLoading?: boolean;
}

ArticleCard.displayName = 'ArticleCard';

export default ArticleCard;
